/**
 * src/features/billing/ui/BillingSettingsEntry.tsx
 *
 * Billing section for the settings/system view.
 * Not-entitled state: shows current plan label, feature list, and upgrade CTA.
 * Entitled state (trial or active Pro): shows plan period and manage subscription button.
 *
 * entitled=true covers both trial users and active-subscription users — D-05 (trial gets
 * full Pro UX). The entitled prop is a UX-only gate; server requireAccess is the real gate.
 *
 * T-08-16: entitled prop is UX-only; component never gates server resources.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/shared/ui/card';
import Button from '@/shared/ui/button';
import { usePortal } from '../lib/usePortal';

export interface BillingSettingsEntryProps {
  /** Whether the user is entitled to the Pro experience (trial or active subscription). UX-only — never an authorization source (T-08-16). */
  entitled: boolean;
  planStatus?: 'active' | 'trialing' | 'canceled' | 'past_due' | string;
  currentPeriodEnd?: number; // Unix timestamp (seconds)
  onUpgradeClick: () => void;
}

// ---------------------------------------------------------------------------
// Free plan sub-component
// ---------------------------------------------------------------------------
function FreePlanState({ onUpgradeClick }: { onUpgradeClick: () => void }) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('billing.current_plan', 'Current plan')}
        </span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {t('billing.plan_free', 'Free')}
        </span>
      </div>

      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
          {t('billing.pro_features_title', "What's included in Pro")}
        </p>
        <ul className="flex flex-col gap-1">
          <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <CheckCircle2 size={14} className="text-green-600 dark:text-green-400 shrink-0" />
            {t('billing.feature_sync', 'Sync across all devices')}
          </li>
          <li className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <CheckCircle2 size={14} className="text-green-600 dark:text-green-400 shrink-0" />
            {t('billing.feature_analytics', 'Margin analytics')}
          </li>
        </ul>
      </div>

      <Button variant="default" size="default" className="w-full" onClick={onUpgradeClick}>
        {t('billing.upgrade_cta', 'Upgrade to Pro')}
      </Button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Pro plan sub-component
// ---------------------------------------------------------------------------
function ProPlanState({
  planStatus,
  currentPeriodEnd,
}: {
  planStatus?: string;
  currentPeriodEnd?: number;
}) {
  const { t, i18n } = useTranslation();
  const { loading, error, handlePortal } = usePortal();

  const formattedDate =
    currentPeriodEnd != null
      ? new Date(currentPeriodEnd * 1000).toLocaleDateString(i18n.language, {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';

  const periodLabel =
    planStatus === 'trialing'
      ? t('billing.trial_until', 'Trial until {{date}}', { date: formattedDate })
      : t('billing.active_until', 'Active until {{date}}', { date: formattedDate });

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {t('billing.current_plan', 'Current plan')}
        </span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          {t('billing.plan_pro', 'Pro')}
        </span>
      </div>

      {formattedDate && (
        <p className="text-sm text-slate-500 dark:text-slate-400">{periodLabel}</p>
      )}

      <Button
        variant="outline"
        size="default"
        className="w-full"
        onClick={handlePortal}
        disabled={loading}
        aria-busy={loading}
      >
        {loading
          ? t('billing.manage_loading', 'Opening portal...')
          : t('billing.manage_subscription', 'Manage subscription')}
      </Button>

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function BillingSettingsEntry({
  entitled,
  planStatus,
  currentPeriodEnd,
  onUpgradeClick,
}: BillingSettingsEntryProps): React.ReactElement {
  return (
    <Card>
      <CardContent className="p-6 flex flex-col gap-4">
        {entitled ? (
          <ProPlanState planStatus={planStatus} currentPeriodEnd={currentPeriodEnd} />
        ) : (
          <FreePlanState onUpgradeClick={onUpgradeClick} />
        )}
      </CardContent>
    </Card>
  );
}
